import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Account, ChartPoint, ChartRange, User } from '@wml-insta/shared';
import { ACTIVE_STATUSES, isArchivedStatus } from '@wml-insta/shared';
import { CHART_RANGES } from '@wml-insta/shared';
import { api } from '../api/client.js';
import { useAccountsData } from '../lib/useAccountsData.js';
import { loadPersisted, savePersisted } from '../lib/persist.js';
import { formatNum } from '../lib/format.js';
import { UpdatedNote } from '../ui/UpdatedNote.js';
import { AccountsTable } from '../ui/AccountsTable.js';
import { AccountFilters, EMPTY_FILTERS, filterAccounts, type AccountFiltersValue } from '../ui/AccountFilters.js';
import { ImportAccountsModal } from '../ui/ImportAccountsModal.js';
import { PhoneTabs } from '../ui/PhoneTabs.js';
import { NameModal } from '../ui/NameModal.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { SnapshotChart } from '../charts/SnapshotChart.js';

function validRange(v: string, fallback: ChartRange = '7d'): ChartRange {
  return (CHART_RANGES as string[]).includes(v) ? (v as ChartRange) : fallback;
}

const RANGES: { key: ChartRange; label: string }[] = [
  { key: '7d', label: '7 дней' },
  { key: '1m', label: 'Месяц' },
  { key: '3m', label: '3 месяца' },
  { key: 'all', label: 'Всё время' },
];

export function PhonePage({ me }: { me: User }) {
  const { id } = useParams();
  const nav = useNavigate();
  const data = useAccountsData();
  // Фильтры сохраняются в localStorage — переживают перезагрузку и переходы.
  const [filters, setFiltersRaw] = useState<AccountFiltersValue>(
    () => ({ ...EMPTY_FILTERS, ...loadPersisted(me.id, 'filters.phone', {}) }),
  );
  const setFilters = (next: AccountFiltersValue) => {
    setFiltersRaw(next);
    savePersisted(me.id, 'filters.phone', next);
  };
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [editingSerial, setEditingSerial] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [chart, setChart] = useState<ChartPoint[]>([]);
  // График: открыт/скрыт и последний таймфрейм — запоминаются на юзера.
  const [range, setRangeRaw] = useState<ChartRange>(() => validRange(loadPersisted<string>(me.id, 'chart.phone.range', '7d')));
  const setRange = (r: ChartRange) => { setRangeRaw(r); savePersisted(me.id, 'chart.phone.range', r); };
  const [showChart, setShowChartRaw] = useState<boolean>(() => loadPersisted<boolean>(me.id, 'chart.phone.open', false));
  const setShowChart = (updater: (v: boolean) => boolean) => {
    setShowChartRaw((v) => {
      const next = updater(v);
      savePersisted(me.id, 'chart.phone.open', next);
      return next;
    });
  };

  const phone = data.phones.find((p) => p.id === id) ?? null;
  const phoneAccounts = useMemo(
    () => data.accounts.filter((a) => a.phoneId === id),
    [data.accounts, id],
  );
  // Архивные (бан/выключение) — в разделе «Архив»; их просмотры при этом
  // остаются в статистике телефона (phone.views24h с сервера считает всех).
  const workingAccounts = useMemo(
    () => phoneAccounts.filter((a) => !isArchivedStatus(a.status) || data.archiveGrace.has(a.id)),
    [phoneAccounts, data.archiveGrace],
  );
  const filtered = useMemo(
    () => filterAccounts(workingAccounts, filters),
    [workingAccounts, filters],
  );

  // График подстраивается под выбранную пачку акков (фильтр по датам старта).
  const chartStartDates = filters.startDates.join(',');
  useEffect(() => {
    if (!id || !showChart) return;
    const extra = chartStartDates ? `&startDates=${encodeURIComponent(chartStartDates)}` : '';
    api<ChartPoint[]>(`/api/stats/chart?range=${range}&phoneId=${id}${extra}`).then(setChart).catch(() => setChart([]));
  }, [id, range, showChart, chartStartDates]);

  if (!data.loaded) return <div className="empty-state">Загрузка…</div>;
  if (!phone) return <div className="empty-state">Телефон не найден.</div>;

  const inWork = phone.byStatus['В работе'] ?? 0;
  const banned = phone.byStatus['Бан'] ?? 0;

  return (
    <div className="page-inner page-fill">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.02em' }}>{phone.name}</h1>
            <button className="rename-btn" onClick={() => setRenaming(true)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
              Переименовать
            </button>
            <button className="rename-btn" onClick={() => setEditingSerial(true)} title="Серийный номер железки — по нему автопостер/капчер находят акки">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M4 7h16M4 12h16M4 17h10" />
              </svg>
              {phone.serial ? `SN: ${phone.serial}` : 'Серийник'}
            </button>
            {me.role === 'admin' && phone.accountCount === 0 && (
              <button className="link-btn danger" onClick={() => setConfirmDelete(true)}>удалить</button>
            )}
          </div>
          <div style={{ color: 'var(--text-subtle)', fontSize: 13, marginTop: 4, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>{phone.accountCount} акк. · в работе {inWork} · банов {banned} · {formatNum(phone.views24h)} просм./24ч</span>
            <UpdatedNote at={phone.lastParsedAt} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" onClick={() => setShowChart((v) => !v)}>
            {showChart ? 'Скрыть график' : 'График'}
          </button>
          <button className="btn-secondary" onClick={() => setImporting(true)}>Импорт из файла</button>
          <button className="btn-primary btn-add-account" onClick={() => setAdding((v) => !v)}>
            {adding ? '✕ Отмена' : '+ Добавить аккаунт'}
          </button>
        </div>
      </div>

      {showChart && (
        <div className="card card-large" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <div className="range-tabs range-tabs-compact">
              {RANGES.map((r) => (
                <button key={r.key} className={range === r.key ? 'active' : ''} onClick={() => setRange(r.key)}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <SnapshotChart points={chart} height={280} />
        </div>
      )}

      {data.error && <div className="error" style={{ marginBottom: 12 }}>{data.error}</div>}

      <AccountsTable
        accounts={filtered}
        me={me}
        phones={data.phones}
        models={data.models}
        tableKey="phone"
          rowActions={filters.unworked ? (a) => (
            a.unworked ? (
              <button
                className="row-open-btn"
                title="Видел: скрыть из необработавших до следующего апдейта отработок"
                style={{ color: 'var(--accent)' }}
                onClick={async () => {
                  const updated = await api<Account>(`/api/accounts/${a.id}/workout-dismiss`, { method: 'POST' });
                  data.applyPatched(updated);
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            ) : null
          ) : undefined}
        hiddenByDefault={['phone']}
        defaultPhoneId={phone.id}
        draftOpen={adding}
        onDraftClose={() => setAdding(false)}
        onPatched={data.applyPatched}
        onDeleted={data.applyDeleted}
        onCreated={(a) => { data.applyCreated(a); }}
        onOpen={(a) => nav(`/account/${a.id}`)}
        onError={data.setError}
        toolbar={
          <AccountFilters
            value={filters}
            onChange={setFilters}
            accounts={workingAccounts}
            models={data.models}
            showPhone={false}
            statusOptions={ACTIVE_STATUSES}
          />
        }
      />

      <PhoneTabs
        phones={data.phones}
        activeId={phone.id}
        onSelect={(id) => nav(id ? `/phone/${id}` : '/accounts')}
      />

      {importing && (
        <ImportAccountsModal
          phones={data.phones}
          models={data.models}
          defaultPhoneId={phone.id}
          onClose={() => setImporting(false)}
          onDone={() => data.reload()}
        />
      )}

      {editingSerial && (
        <NameModal
          title="Серийный номер телефона"
          label="Серийник (пусто — убрать)"
          initial={phone.serial ?? ''}
          allowEmpty
          onClose={() => setEditingSerial(false)}
          onSubmit={async (serial) => {
            await api(`/api/phones/${phone.id}`, { method: 'PATCH', body: JSON.stringify({ serial: serial || null }) });
            data.reload();
          }}
        />
      )}

      {renaming && (
        <NameModal
          title="Переименовать телефон"
          label="Название"
          initial={phone.name}
          onClose={() => setRenaming(false)}
          onSubmit={async (name) => {
            await api(`/api/phones/${phone.id}`, { method: 'PATCH', body: JSON.stringify({ name }) });
            data.reload();
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Удалить телефон?"
          message={<>Телефон <b>{phone.name}</b> будет удалён.</>}
          confirmLabel="Удалить"
          danger
          onConfirm={async () => {
            await api(`/api/phones/${phone.id}`, { method: 'DELETE' });
            nav('/phones');
          }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
